import React from 'react';
import { useClaimRequest } from '../hooks/usePayment';
import { useTelcoPaymentMethods } from '../hooks/usePayment';
import { useInstantClaimRequest } from '../hooks/useInstantClaimRequest';
import { useWithdrawalReasons } from '../hooks/useWithdrawalReasons';
import { usePensionsSummary } from '../hooks/dashboard/usePensionSummary';
import { usePolicySummary } from '../hooks/dashboard/usePolicySummary';
import { notifyUser, normalizeGhanaPhone, validateGhanaPhone } from '../util/util';
import { useModalWidth } from '../hooks/useModalWidth';
import { ChevronRight, Check } from 'lucide-react';

const toTitleCase = (str) => str ? str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : '';

// Step definitions
const STEPS = {
  SELECT_TYPE: 'select_type',
  SELECT_CLAIM_METHOD: 'select_claim_method',
  INSTANT_CLAIM_AMOUNT: 'instant_claim_amount',
  STANDARD_CLAIM_AMOUNT: 'standard_claim_amount',
  PAYMENT_METHOD: 'payment_method',
  MOBILE_MONEY_DETAILS: 'mobile_money_details',
  BANK_PAYMENT_DETAILS: 'bank_payment_details',
  REFUND_REQUEST: 'refund_request',
  REFUND_REVIEW: 'refund_review',
  SUMMARY: 'summary',
  SUCCESS: 'success',
};

const ClaimsModal = ({ isOpen, onClose, policyNumber, productType, policyName, availableCashValue }) => {
  // eslint-disable-next-line no-unused-vars
  const modalWidth = useModalWidth(); // Kept for future responsive use
  const [currentStep, setCurrentStep] = React.useState(STEPS.SELECT_TYPE);
  const [claimType, setClaimType] = React.useState(null); // 'partial_withdrawal' | 'refund_claim'
  const [claimMethod, setClaimMethod] = React.useState(null); // 'instant' | 'standard'
  const [claimAmount, setClaimAmount] = React.useState('');
  const [telecomMethod, setTelecomMethod] = React.useState('');
  const [momoWallet, setMomoWallet] = React.useState('');
  const [amountError, setAmountError] = React.useState('');
  const [phoneError, setPhoneError] = React.useState('');
  const [claimResponse, setClaimResponse] = React.useState(null);
  
  // Standard claim - payment method and bank details
  const [paymentMethod, setPaymentMethod] = React.useState(null); // 'mobile_money' | 'bank_payment'
  const [selectedBank, setSelectedBank] = React.useState('');
  const [selectedBranch, setSelectedBranch] = React.useState('');
  const [accountNumber, setAccountNumber] = React.useState('');
  const [accountName, setAccountName] = React.useState('');
  
  // Refund claim details
  const [refundReason, setRefundReason] = React.useState('');
  
  // Instant claim - withdrawal purpose
  const [withdrawalPurpose, setWithdrawalPurpose] = React.useState('');

  const claimRequestMutation = useClaimRequest();
  const instantClaimMutation = useInstantClaimRequest();
  const { data: telcoMethodsData, isLoading: isLoadingTelcoMethods } = useTelcoPaymentMethods();
  const { data: withdrawalReasonsData, isLoading: isLoadingWithdrawalReasons } = useWithdrawalReasons();
  const { data: pensionsSummaryData } = usePensionsSummary();
  const { data: policySummaryData } = usePolicySummary();

  // Determine policy type from prop
  const isPensionPolicy = productType === 'PENSION';
  const isLifePolicy = productType === 'LIFE';

  // Get cash value - use prop if provided (including 0), otherwise calculate from hooks
  let currentCashValue = availableCashValue !== undefined ? availableCashValue : 0;
  if (availableCashValue === undefined || availableCashValue === null) {
    if (isPensionPolicy) {
      currentCashValue = policySummaryData?.data?.total_available_balance || 
                        pensionsSummaryData?.data?.total_investment || 0;
    } else if (isLifePolicy) {
      currentCashValue = policySummaryData?.data?.total_life_investment || 0;
    } else {
      currentCashValue = policySummaryData?.data?.total_available_balance || 
                        pensionsSummaryData?.data?.total_investment || 0;
    }
  }

  const telcoMethods = React.useMemo(() => telcoMethodsData?.data || [], [telcoMethodsData]);

  // Set default telecom method when data loads
  React.useEffect(() => {
    if (telcoMethods.length > 0 && !telecomMethod) {
      // Default to Vodafone (00465) or first available
      const vodafone = telcoMethods.find(m => m.emp_code === '00465');
      setTelecomMethod(vodafone?.emp_code || telcoMethods[0]?.emp_code);
    }
  }, [telcoMethods, telecomMethod]);

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep(STEPS.SELECT_TYPE);
      setClaimType(null);
      setClaimMethod(null);
      setClaimAmount('');
      setMomoWallet('');
      setAmountError('');
      setPhoneError('');
      setClaimResponse(null);
      setPaymentMethod(null);
      setSelectedBank('');
      setSelectedBranch('');
      setAccountNumber('');
      setAccountName('');
      setRefundReason('');
      setWithdrawalPurpose('');
    }
  }, [isOpen]);

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'GHS 0.00';
    const num = Number(value);
    return `GHS ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate max claimable based on method
  const getMaxClaimable = () => {
    if (claimMethod === 'instant') {
      // Max 40% of cash value, up to GH₵3,000
      const fortyPercent = currentCashValue * 0.4;
      return Math.min(fortyPercent, 3000);
    } else if (claimMethod === 'standard') {
      // Max 50% of cash value, up to GH₵3,000
      const fiftyPercent = currentCashValue * 0.5;
      return Math.min(fiftyPercent, 3000);
    }
    return currentCashValue;
  };

  // Get claim method display name
  const getClaimMethodDisplay = () => {
    if (claimMethod === 'instant') return 'Instant';
    if (claimMethod === 'standard') return 'Standard';
    return '';
  };

  // Calculate processing charge
  const getProcessingCharge = () => {
    if (claimMethod === 'instant') {
      const amount = parseFloat(claimAmount) || 0;
      return amount * 0.02;
    }
    return 0;
  };

  // Calculate net amount
  const getNetAmount = () => {
    const amount = parseFloat(claimAmount) || 0;
    return amount - getProcessingCharge();
  };

  // Validate amount
  const handleAmountChange = (value) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    const parts = sanitizedValue.split('.');
    const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitizedValue;
    const numValue = parseFloat(cleanValue) || 0;
    const maxClaimable = getMaxClaimable();

    if (numValue > maxClaimable) {
      setAmountError(`Amount cannot exceed maximum claimable of ${formatCurrency(maxClaimable)}`);
    } else if (numValue <= 0 && cleanValue !== '') {
      setAmountError('Amount must be greater than 0');
    } else {
      setAmountError('');
    }

    setClaimAmount(cleanValue);
  };

  // Handle phone number input
  const handlePhoneChange = (value) => {
    const normalized = normalizeGhanaPhone(value);
    setMomoWallet(normalized);

    if (normalized.length === 12) {
      const validation = validateGhanaPhone(normalized);
      setPhoneError(validation.error || '');
    } else if (normalized.length > 0 && normalized.length < 12) {
      setPhoneError('Phone number must be exactly 12 digits (233 + 9 digits)');
    } else {
      setPhoneError('');
    }
  };

  // Get selected telco name
  const getSelectedTelcoName = () => {
    const method = telcoMethods.find(m => m.emp_code === telecomMethod);
    return method ? toTitleCase(method.Name) : 'Mobile Money';
  };

  // Handle claim submission
  const handleSubmitClaim = () => {
    // Use instant claim API for instant claims
    if (claimMethod === 'instant') {
      instantClaimMutation.mutate({
        policyNumber,
        currentCashValue,
        claimAmount,
        withdrawalPurpose,
        telecomMethod,
        momoWallet,
      }, {
        onSuccess: (response) => {
          setClaimResponse(response?.data || {
            reference: 'SLAMS-' + Math.random().toString(36).substring(2, 10).toUpperCase(),
          });
          setCurrentStep(STEPS.SUCCESS);
        },
        onError: (error) => {
          notifyUser('error', error?.message || 'Failed to submit claim request. Please try again.');
        },
      });
    } else {
      // Use standard claim API for standard claims
      claimRequestMutation.mutate({
        policyNumber,
        currentCashValue,
        claimAmount,
        telecomMethod,
        momoWallet,
      }, {
        onSuccess: (response) => {
          setClaimResponse(response?.data || {
            reference: 'SLAMS-' + Math.random().toString(36).substring(2, 10).toUpperCase(),
          });
          setCurrentStep(STEPS.SUCCESS);
        },
        onError: (error) => {
          notifyUser('error', error?.message || 'Failed to submit claim request. Please try again.');
        },
      });
    }
  };

  // Navigation handlers
  const handleSelectClaimType = (type) => {
    setClaimType(type);
    if (type === 'partial_withdrawal') {
      setCurrentStep(STEPS.SELECT_CLAIM_METHOD);
    } else {
      // For refund claim
      setCurrentStep(STEPS.REFUND_REQUEST);
    }
  };

  const handleSelectClaimMethod = (method) => {
    setClaimMethod(method);
    if (method === 'instant') {
      setCurrentStep(STEPS.INSTANT_CLAIM_AMOUNT);
    } else {
      // For standard claim - go to standard claim amount step
      setCurrentStep(STEPS.STANDARD_CLAIM_AMOUNT);
    }
  };

  const handleContinueFromAmount = () => {
    const numValue = parseFloat(claimAmount) || 0;
    const maxClaimable = getMaxClaimable();

    if (numValue > maxClaimable) {
      notifyUser('error', `Amount cannot exceed maximum claimable of ${formatCurrency(maxClaimable)}`);
      return;
    }
    if (numValue <= 0) {
      notifyUser('error', 'Please enter a valid amount');
      return;
    }
    setCurrentStep(STEPS.MOBILE_MONEY_DETAILS);
  };

  const handleContinueFromMomo = () => {
    if (phoneError) {
      notifyUser('error', phoneError);
      return;
    }
    if (!momoWallet || momoWallet.length !== 12) {
      notifyUser('error', 'Please enter a valid phone number');
      return;
    }
    if (!telecomMethod) {
      notifyUser('error', 'Please select a telco operator');
      return;
    }
    setCurrentStep(STEPS.SUMMARY);
  };

  const handleBack = () => {
    switch (currentStep) {
      case STEPS.SELECT_CLAIM_METHOD:
      case STEPS.REFUND_REQUEST:
        setCurrentStep(STEPS.SELECT_TYPE);
        break;
      case STEPS.INSTANT_CLAIM_AMOUNT:
      case STEPS.STANDARD_CLAIM_AMOUNT:
        setCurrentStep(STEPS.SELECT_CLAIM_METHOD);
        break;
      case STEPS.PAYMENT_METHOD:
        setCurrentStep(STEPS.STANDARD_CLAIM_AMOUNT);
        break;
      case STEPS.MOBILE_MONEY_DETAILS:
        if (claimMethod === 'standard') {
          setCurrentStep(STEPS.PAYMENT_METHOD);
        } else {
          setCurrentStep(STEPS.INSTANT_CLAIM_AMOUNT);
        }
        break;
      case STEPS.BANK_PAYMENT_DETAILS:
        setCurrentStep(STEPS.PAYMENT_METHOD);
        break;
      case STEPS.REFUND_REVIEW:
        setCurrentStep(STEPS.REFUND_REQUEST);
        break;
      case STEPS.SUMMARY:
        if (paymentMethod === 'bank_payment') {
          setCurrentStep(STEPS.BANK_PAYMENT_DETAILS);
        } else {
          setCurrentStep(STEPS.MOBILE_MONEY_DETAILS);
        }
        break;
      default:
        break;
    }
  };

  const handleClose = () => {
    onClose();
  };

  // omk-text-field styles for error states
  const getTextFieldStyles = (hasError) => ({
    width: '100%',
    '--omk-textfield-height': '48px',
    '--omk-textfield-border-radius': '10px',
    '--omk-textfield-padding': '0 16px',
    '--omk-textfield-background': '#fff',
    '--omk-textfield-border-color': hasError ? '#ff4d4f' : '#ddd',
    '--omk-textfield-hover-border-color': '#0D7C4B',
  });

  // Render step 1: Select Claim Type
  const renderSelectClaimType = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Partial Withdrawal Option */}
        <button
          onClick={() => handleSelectClaimType('partial_withdrawal')}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 4 }}>
              Partial Withdrawal
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Withdrawal a portion of your cash value
            </div>
          </div>
          <ChevronRight size={20} color="#888" />
        </button>

        {/* Refund Claim Option */}
        <button
          onClick={() => handleSelectClaimType('refund_claim')}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 4 }}>
              Refund Claim
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Request reimbursement under policy guidelines
            </div>
          </div>
          <ChevronRight size={20} color="#888" />
        </button>
      </div>
    </div>
  );

  // Render step 2: Select Claim Method (Instant vs Standard)
  const renderSelectClaimMethod = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Instant Claims Option */}
        <button
          onClick={() => handleSelectClaimMethod('instant')}
          style={{ 
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 8 }}>
              Instant Claims
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Get funds immediately via mobile money
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>Max 40% of cash value (up to GH₵3,000)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>2% processing charge applies</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>Mobile money disbursement only</span>
              </div>
            </div>
          </div>
          <ChevronRight size={20} color="#888" style={{ marginTop: 4 }} />
        </button>

        {/* Standard Claim Option */}
        <button
          onClick={() => handleSelectClaimMethod('standard')}
          style={{ 
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 8 }}>
              Standard Claim
            </div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Get funds immediately via mobile money
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>Max 50% of cash value (up to GH₵3,000)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>No monetary caps or charges</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555' }}>
                <Check size={14} color="#0D7C4B" />
                <span>Mobile money or Bank Payment</span>
              </div>
            </div>
          </div>
          <ChevronRight size={20} color="#888" style={{ marginTop: 4 }} />
        </button>
      </div>
    </div>
  );

  // Render step 3: Instant Claim Amount
  const renderInstantClaimAmount = () => {
    const maxClaimable = getMaxClaimable();
    const withdrawalReasons = withdrawalReasonsData?.data || [];
    
    return (
      <div style={{ padding: '24px 32px 32px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Amount Input */}
          <div>
            <omk-text-field
              label="Enter the amount you want to claim"
              name="claim_amount"
              type="text"
              value={claimAmount}
              onInput={(e) => handleAmountChange(e.target.value)}
              required
              style={getTextFieldStyles(amountError)}
              placeholder="3,000"
            />
            {amountError && (
              <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                {amountError}
              </div>
            )}
          </div>

          {/* Withdrawal Purpose Dropdown */}
          <div>
            <omk-select
              label="Purpose of Withdrawal"
              name="withdrawal_purpose"
              value={withdrawalPurpose}
              onInput={(e) => setWithdrawalPurpose(e.target.value)}
              disabled={isLoadingWithdrawalReasons}
            >
              {isLoadingWithdrawalReasons ? (
                <omk-select-option value="">Loading...</omk-select-option>
              ) : withdrawalReasons.length > 0 ? (
                <>
                  <omk-select-option value="">Select purpose</omk-select-option>
                  {[...withdrawalReasons].sort((a, b) => {
                    const aText = (a.Description || a.description || a.name || a.label || '').toLowerCase();
                    const bText = (b.Description || b.description || b.name || b.label || '').toLowerCase();
                    return aText.localeCompare(bText);
                  }).map((reason) => (
                    <omk-select-option key={reason.id} value={reason.id}>
                      {reason.Description || reason.description || reason.name || reason.label}
                    </omk-select-option>
                  ))}
                </>
              ) : (
                <omk-select-option value="">No purposes available</omk-select-option>
              )}
            </omk-select>
          </div>

          {/* Info Display */}
          <div style={{ 
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Available cash value</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>
                {formatCurrency(currentCashValue)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Maximum claimable</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#0D7C4B' }}>
                {formatCurrency(maxClaimable)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Processing charge</span>
              <span style={{ fontSize: 14, color: '#555' }}>2% of claim amount</span>
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinueFromAmount}
            className="btn btn-primary"
            disabled={!claimAmount || amountError || !withdrawalPurpose}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  // Render step: Standard Claim Amount
  const renderStandardClaimAmount = () => {
    const maxClaimable = getMaxClaimable();
    
    return (
      <div style={{ padding: '24px 32px 32px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Amount Input */}
          <div>
            <omk-text-field
              label="Enter the amount you want to claim"
              name="claim_amount"
              type="text"
              value={claimAmount}
              onInput={(e) => handleAmountChange(e.target.value)}
              required
              style={getTextFieldStyles(amountError)}
              placeholder="5,000"
            />
            {amountError && (
              <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                {amountError}
              </div>
            )}
          </div>

          {/* Info Display */}
          <div style={{ 
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Available cash value</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>
                {formatCurrency(currentCashValue)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Maximum claimable</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#0D7C4B' }}>
                {formatCurrency(maxClaimable)}
              </span>
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={() => {
              const numValue = parseFloat(claimAmount) || 0;
              const maxClaimable = getMaxClaimable();
              if (numValue > maxClaimable) {
                notifyUser('error', `Amount cannot exceed maximum claimable of ${formatCurrency(maxClaimable)}`);
                return;
              }
              if (numValue <= 0) {
                notifyUser('error', 'Please enter a valid amount');
                return;
              }
              setCurrentStep(STEPS.PAYMENT_METHOD);
            }}
            className="btn btn-primary"
            disabled={!claimAmount || amountError}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  // Render step: Payment Method Selection
  const renderPaymentMethod = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Mobile Money Option */}
        <button
          onClick={() => {
            setPaymentMethod('mobile_money');
            setCurrentStep(STEPS.MOBILE_MONEY_DETAILS);
          }}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 4 }}>
              Mobile Money
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Withdrawal to your phone number
            </div>
          </div>
          <ChevronRight size={20} color="#888" />
        </button>

        {/* Bank Payment Option */}
        <button
          onClick={() => {
            setPaymentMethod('bank_payment');
            setCurrentStep(STEPS.BANK_PAYMENT_DETAILS);
          }}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#0D7C4B';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(13, 124, 75, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e0e0e0';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#222', marginBottom: 4 }}>
              Bank Payment
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              Withdrawal to your local bank account
            </div>
          </div>
          <ChevronRight size={20} color="#888" />
        </button>
      </div>
    </div>
  );

  // Render step: Bank Payment Details
  const renderBankPaymentDetails = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Bank Dropdown */}
        <div>
          <omk-select
            label="Select bank"
            name="bank"
            value={selectedBank}
            onInput={(e) => setSelectedBank(e.target.value)}
          >
            <omk-select-option value="">Select bank</omk-select-option>
            <omk-select-option value="ecobank">Ecobank Ghana</omk-select-option>
            <omk-select-option value="gtbank">GTBank Ghana</omk-select-option>
            <omk-select-option value="fidelity">Fidelity Bank</omk-select-option>
            <omk-select-option value="calbank">CalBank</omk-select-option>
            <omk-select-option value="gcb">GCB Bank</omk-select-option>
            <omk-select-option value="absa">Absa Bank</omk-select-option>
            <omk-select-option value="standard_chartered">Standard Chartered</omk-select-option>
            <omk-select-option value="uba">UBA Ghana</omk-select-option>
            <omk-select-option value="zenith">Zenith Bank</omk-select-option>
          </omk-select>
        </div>

        {/* Branch Dropdown */}
        <div>
          <omk-select
            label="Select branch"
            name="branch"
            value={selectedBranch}
            onInput={(e) => setSelectedBranch(e.target.value)}
            disabled={!selectedBank}
          >
            <omk-select-option value="">Select branch</omk-select-option>
            <omk-select-option value="accra_main">Accra Main</omk-select-option>
            <omk-select-option value="adabraka">Adabraka</omk-select-option>
            <omk-select-option value="airport">Airport City</omk-select-option>
            <omk-select-option value="east_legon">East Legon</omk-select-option>
            <omk-select-option value="osu">Osu</omk-select-option>
            <omk-select-option value="spintex">Spintex</omk-select-option>
            <omk-select-option value="tema">Tema</omk-select-option>
          </omk-select>
        </div>

        {/* Account Number Input */}
        <div>
          <omk-text-field
            label="Enter account number"
            name="account_number"
            type="text"
            value={accountNumber}
            onInput={(e) => setAccountNumber(e.target.value.replace(/[^0-9]/g, ''))}
            required
            style={getTextFieldStyles(false)}
            placeholder="Enter account number"
          />
        </div>

        {/* Account Name Display (Read-only) */}
        <div>
          <omk-text-field
            label="Account Name"
            name="account_name"
            type="text"
            value={accountName || 'Kwame Asante'}
            readOnly
            style={{
              ...getTextFieldStyles(false),
              '--omk-textfield-background': '#f5f5f5',
            }}
            placeholder="Account name will appear here"
          />
        </div>

        {/* Continue Button */}
        <button
          onClick={() => {
            if (!selectedBank) {
              notifyUser('error', 'Please select a bank');
              return;
            }
            if (!selectedBranch) {
              notifyUser('error', 'Please select a branch');
              return;
            }
            if (!accountNumber || accountNumber.length < 10) {
              notifyUser('error', 'Please enter a valid account number');
              return;
            }
            setCurrentStep(STEPS.SUMMARY);
          }}
          className="btn btn-primary"
          disabled={!selectedBank || !selectedBranch || !accountNumber}
        >
          Continue
        </button>
      </div>
    </div>
  );

  // Render step 4: Mobile Money Details
  const renderMobileMoneyDetails = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Telco Operator Dropdown */}
        <div>
          <omk-select
            label="Select Telco Operator"
            name="telco_method"
            value={telecomMethod}
            onInput={(e) => setTelecomMethod(e.target.value)}
            disabled={isLoadingTelcoMethods}
          >
            {isLoadingTelcoMethods ? (
              <omk-select-option value="">Loading...</omk-select-option>
            ) : telcoMethods.length > 0 ? (
              telcoMethods.map((method) => (
                <omk-select-option key={method.emp_code} value={method.emp_code}>
                  {toTitleCase(method.Name)}
                </omk-select-option>
              ))
            ) : (
              <omk-select-option value="">No payment methods available</omk-select-option>
            )}
          </omk-select>
        </div>

        {/* Phone Number Input */}
        <div>
          <omk-text-field
            label="Registered Phone Number"
            name="momo_wallet"
            type="tel"
            value={momoWallet}
            onInput={(e) => handlePhoneChange(e.target.value)}
            required
            style={getTextFieldStyles(phoneError)}
            placeholder="05XXXXXXXX or 233XXXXXXXXX"
            maxLength="12"
            pattern="[0-9]*"
            inputMode="numeric"
          />
          {phoneError && (
            <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
              {phoneError}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div style={{
          background: '#FFF8E7',
          border: '1px solid #FFE4B5',
          borderRadius: 10,
          padding: '16px',
        }}>
          <p style={{ margin: 0, fontSize: 13, color: '#8B7355', lineHeight: 1.5 }}>
            Identity validation will be performed via Hubtel. At least two names must match in any order.
          </p>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinueFromMomo}
          className="btn btn-primary"
          disabled={!momoWallet || phoneError || !telecomMethod}
        >
          Continue
        </button>
      </div>
    </div>
  );

  // Render step 5: Summary
  const renderSummary = () => {
    // Get payment display info
    const getPaymentDisplay = () => {
      if (paymentMethod === 'bank_payment') {
        const bankNames = {
          ecobank: 'Ecobank Ghana',
          gtbank: 'GTBank Ghana',
          fidelity: 'Fidelity Bank',
          calbank: 'CalBank',
          gcb: 'GCB Bank',
          absa: 'Absa Bank',
          standard_chartered: 'Standard Chartered',
          uba: 'UBA Ghana',
          zenith: 'Zenith Bank',
        };
        return `${bankNames[selectedBank] || selectedBank} — ${accountNumber}`;
      }
      return `${getSelectedTelcoName()} — ${momoWallet}`;
    };

    // Get withdrawal purpose display name
    const getWithdrawalPurposeDisplay = () => {
      const reasons = withdrawalReasonsData?.data || [];
      const selectedReason = reasons.find(r => r.id === withdrawalPurpose || r.id === parseInt(withdrawalPurpose));
      return selectedReason?.Description || selectedReason?.description || selectedReason?.name || selectedReason?.label || 'N/A';
    };

    const isSubmitting = claimRequestMutation.isLoading || instantClaimMutation.isLoading;

    return (
      <div style={{ padding: '24px 32px 32px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Summary Details */}
          <div style={{ 
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Policy</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
                {policyName || 'SmartLife Investment Plan (SIP)'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Claim Type</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>
                Partial Withdrawal — {getClaimMethodDisplay()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Claim Amount</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>
                {formatCurrency(parseFloat(claimAmount) || 0)}
              </span>
            </div>
            {claimMethod === 'instant' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#666' }}>Purpose</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
                  {getWithdrawalPurposeDisplay()}
                </span>
              </div>
            )}
            {claimMethod === 'instant' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#666' }}>Charge (2%)</span>
                <span style={{ fontSize: 14, color: '#222' }}>
                  - {formatCurrency(getProcessingCharge())}
                </span>
              </div>
            )}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              paddingTop: 12,
              borderTop: '1px solid #e0e0e0',
            }}>
              <span style={{ fontSize: 14, color: '#666' }}>Net Amount</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#0D7C4B' }}>
                {formatCurrency(getNetAmount())}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: '#666' }}>Payment</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
                {getPaymentDisplay()}
              </span>
            </div>
          </div>

          {/* Disclaimer */}
          <p style={{ margin: 0, fontSize: 12, color: '#888', lineHeight: 1.5 }}>
            {claimMethod === 'instant' 
              ? 'By submitting, your claim will be processed immediately through Hubtel and a reference generated via SLAMS.'
              : 'By submitting, your claim will be sent to SLAMS for review and processing by a claims officer.'}
          </p>

          {/* Process Claim Button */}
          <button
            onClick={handleSubmitClaim}
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Process Claim'}
          </button>
        </div>
      </div>
    );
  };

  // Render step: Refund Request
  const renderRefundRequest = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Manual Processing Warning */}
        <div style={{
          background: '#FFF8E7',
          border: '1px solid #FFE4B5',
          borderRadius: 10,
          padding: '16px',
        }}>
          <p style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600, color: '#8B7355' }}>
            Manual Processing
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#8B7355', lineHeight: 1.5 }}>
            Refund claims are reviewed by the claims operations team. You may be contacted for supporting documentation.
          </p>
        </div>

        {/* Refund Amount Input */}
        <div>
          <omk-text-field
            label="Refund Amount"
            name="refund_amount"
            type="text"
            value={claimAmount}
            onInput={(e) => handleAmountChange(e.target.value)}
            required
            style={getTextFieldStyles(amountError)}
            placeholder="Enter amount"
          />
          {amountError && (
            <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
              {amountError}
            </div>
          )}
        </div>

        {/* Reason Textarea - using standard textarea since omk-text-field doesn't support multiline */}
        <div>
          <label style={{ display: 'block', fontSize: 14, color: '#666', marginBottom: 8 }}>
            Describe the reason for refund request
          </label>
          <textarea
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="Enter your reason here..."
            rows={4}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #ddd',
              borderRadius: 10,
              fontSize: 15,
              outline: 'none',
              resize: 'none',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Continue Button */}
        <button
          onClick={() => {
            const numValue = parseFloat(claimAmount) || 0;
            if (numValue <= 0) {
              notifyUser('error', 'Please enter a valid amount');
              return;
            }
            if (!refundReason.trim()) {
              notifyUser('error', 'Please provide a reason for the refund request');
              return;
            }
            setCurrentStep(STEPS.REFUND_REVIEW);
          }}
          className="btn btn-primary"
          disabled={!claimAmount || amountError || !refundReason.trim()}
        >
          Continue
        </button>
      </div>
    </div>
  );

  // Render step: Refund Review
  const renderRefundReview = () => (
    <div style={{ padding: '24px 32px 32px 32px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Review Details */}
        <div style={{ 
          background: '#f9f9f9', 
          borderRadius: 12, 
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: '#666' }}>Policy</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#222', textAlign: 'right' }}>
              {policyName || 'SmartLife Investment Plan (SIP)'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: '#666' }}>Claim Type</span>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#222' }}>
              Refund — Standard
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 14, color: '#666' }}>Amount</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>
              {formatCurrency(parseFloat(claimAmount) || 0)}
            </span>
          </div>
        </div>

        {/* Disclaimer */}
        <p style={{ margin: 0, fontSize: 12, color: '#888', lineHeight: 1.5 }}>
          By submitting, you confirm that the information provided is accurate. The claims team will review your request.
        </p>

        {/* Continue Button */}
        <button
          onClick={handleSubmitClaim}
          className="btn btn-primary"
          disabled={claimRequestMutation.isLoading}
        >
          {claimRequestMutation.isLoading ? 'Submitting...' : 'Continue'}
        </button>
      </div>
    </div>
  );

  // Render step 6: Success
  const renderSuccess = () => {
    // Determine success message based on claim type
    const getSuccessTitle = () => {
      if (claimMethod === 'instant') return 'Claim Processed!';
      return 'Claim Submitted!';
    };

    const getSuccessMessage = () => {
      if (claimMethod === 'instant') {
        return 'Your withdrawal has been processed through Hubtel.';
      }
      return 'Your claim has been submitted to SLAMS for processing.';
    };

    const getClaimTypeDisplay = () => {
      if (claimType === 'refund_claim') {
        return 'Refund — Standard';
      }
      return `Partial Withdrawal — ${getClaimMethodDisplay()}`;
    };

    const isInstant = claimMethod === 'instant';

    return (
      <div style={{ padding: '32px 32px 40px 32px' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 24,
        }}>
          {/* Success Icon */}
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0D7C4B 0%, #00b050 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Check size={40} color="#fff" strokeWidth={3} />
          </div>

          {/* Success Message */}
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 700, color: '#222' }}>
              {getSuccessTitle()}
            </h3>
            <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
              {getSuccessMessage()}
            </p>
          </div>

          {/* Transaction Details */}
          <div style={{ 
            width: '100%',
            background: '#f9f9f9', 
            borderRadius: 12, 
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#666' }}>Reference</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>
                {claimResponse?.reference || `SLAMS-${Math.random().toString(36).substring(2, 10).toUpperCase()}XXX`}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#666' }}>Policy</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#222' }}>
                {policyName || 'SmartLife Investment Plan'} ({policyNumber || 'POL-001'})
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#666' }}>Claim Type</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#222' }}>
                {getClaimTypeDisplay()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: '#666' }}>
                {isInstant ? 'Claim Amount' : 'Amount'}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#222' }}>
                {formatCurrency(parseFloat(claimAmount) || 0)}
              </span>
            </div>
            {isInstant && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#666' }}>Charge (2%)</span>
                <span style={{ fontSize: 13, color: '#222' }}>
                  - {formatCurrency(getProcessingCharge())}
                </span>
              </div>
            )}
            {isInstant && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                paddingTop: 10,
                borderTop: '1px solid #e0e0e0',
              }}>
                <span style={{ fontSize: 13, color: '#666' }}>Net Amount</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#0D7C4B' }}>
                  {formatCurrency(getNetAmount())}
                </span>
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  // Get title based on current step
  const getTitle = () => {
    switch (currentStep) {
      case STEPS.SELECT_TYPE:
        return 'Select Claim Type';
      case STEPS.SELECT_CLAIM_METHOD:
        return 'Partial Withdrawal';
      case STEPS.INSTANT_CLAIM_AMOUNT:
        return 'Instant Claims';
      case STEPS.STANDARD_CLAIM_AMOUNT:
        return 'Standard Claims';
      case STEPS.PAYMENT_METHOD:
        return 'Payment Method';
      case STEPS.MOBILE_MONEY_DETAILS:
        return 'Mobile money';
      case STEPS.BANK_PAYMENT_DETAILS:
        return 'Bank Payment';
      case STEPS.REFUND_REQUEST:
        return 'Refund Request';
      case STEPS.REFUND_REVIEW:
        return 'Review';
      case STEPS.SUMMARY:
        return 'Summary';
      case STEPS.SUCCESS:
        return '';
      default:
        return 'Claim';
    }
  };

  // Render current step content
  const renderContent = () => {
    switch (currentStep) {
      case STEPS.SELECT_TYPE:
        return renderSelectClaimType();
      case STEPS.SELECT_CLAIM_METHOD:
        return renderSelectClaimMethod();
      case STEPS.INSTANT_CLAIM_AMOUNT:
        return renderInstantClaimAmount();
      case STEPS.STANDARD_CLAIM_AMOUNT:
        return renderStandardClaimAmount();
      case STEPS.PAYMENT_METHOD:
        return renderPaymentMethod();
      case STEPS.MOBILE_MONEY_DETAILS:
        return renderMobileMoneyDetails();
      case STEPS.BANK_PAYMENT_DETAILS:
        return renderBankPaymentDetails();
      case STEPS.REFUND_REQUEST:
        return renderRefundRequest();
      case STEPS.REFUND_REVIEW:
        return renderRefundReview();
      case STEPS.SUMMARY:
        return renderSummary();
      case STEPS.SUCCESS:
        return renderSuccess();
      default:
        return renderSelectClaimType();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="claims-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.18)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <style>{`
        @media (max-width: 768px) {
          .claims-modal-overlay {
            align-items: flex-start !important;
            padding-top: 40px !important;
          }
        }
      `}</style>
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 420,
          maxHeight: '85vh',
          overflowY: 'auto',
          padding: 0,
          boxShadow: '0 16px 60px rgba(0,0,0,0.15)'
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        {currentStep !== STEPS.SUCCESS && (
          <div style={{ padding: '24px 32px 0 32px' }}>
            <div 
              className="modal-header-title" 
              style={{ 
                fontWeight: 700, 
                color: '#222', 
                fontSize: 18,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between' 
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {currentStep !== STEPS.SELECT_TYPE && (
                  <span
                    onClick={handleBack}
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#666',
                    }}
                  >
                    <ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
                  </span>
                )}
                <span>{getTitle()}</span>
              </div>
              <span 
                onClick={onClose} 
                style={{ 
                  cursor: 'pointer',
                  fontSize: 24,
                  fontWeight: 300,
                  color: '#888',
                  lineHeight: 1,
                }} 
                aria-label="Close"
              >
                ×
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default ClaimsModal;
